import { useState, useEffect } from "react"
import { IoError, ioErrorOf } from "../../../app/data/errors"
import { useAppSelector } from "../../../app/hooks"
import { selectUserId } from "../../../app/data/appTokensStore"
import {
  useGetUserInfoWritableQuery,
  useUpdateUserSettingsMutation,
} from "../../../app/data/userDataSlice/userApi"
import {
  UserInfoWritable,
  UserProfileUpdate,
} from "../../../app/data/userDataSlice/types"
import { ResponseStatus } from "../../../shared/user-profile/types"

export type UpdateProfileArgs = Omit<UserProfileUpdate, "user_id">
export type SettingsEditor = {
  updateProfile: (nextState: Partial<UserProfileUpdate>) => void
  isLoading: boolean
  error?: IoError
  currentUser?: UserInfoWritable
}

const createInitialProfileState = (id?: number): UserProfileUpdate => ({
  birthday: "",
  first_name: "",
  last_name: "",
  user_id: id,
  username: "",
  is_geolocation_available: false,
  interesting_event_type: [],
})

export const useProfileSettings = (): ResponseStatus<UserInfoWritable> => {
  const userId = useAppSelector(selectUserId)!
  const { data, error, isLoading } = useGetUserInfoWritableQuery({ userId })

  return { isLoading, error: ioErrorOf(error), state: data }
}

export const useProfileSettingsEditor = (): SettingsEditor => {
  const userId = useAppSelector(selectUserId)!
  const { data, error, isLoading } = useGetUserInfoWritableQuery({ userId })
  const [profileState, setProfileState] = useState<UserProfileUpdate>(
    createInitialProfileState(Number(userId)),
  )
  const [updateProfile] = useUpdateUserSettingsMutation()

  useEffect(() => {
    if (data) {
      setProfileState((prevState) => ({
        ...prevState,
        birthday: data.birthday,
        first_name: data.first_name,
        last_name: data.last_name,
        username: data.username,
        is_geolocation_available: data.is_geolocation_available,
        interesting_event_type: data.interesting_event_type.map(
          (event) => event.id,
        ),
      }))
    }
  }, [data])

  const handleUpdateProfile = (nextState: Partial<UserProfileUpdate>) => {
    updateProfile({ ...profileState, ...nextState })
  }

  return {
    updateProfile: handleUpdateProfile,
    isLoading,
    error: ioErrorOf(error),
    currentUser: data,
  }
}
